import Select from 'react-select';
import React, { useState } from 'react';
import { locationPreferenceOptions } from 'shared-utils';
import { Button, FileInput, Label, Radio, TextInput } from 'flowbite-react';
import { ProfileFormData, ScoreType, UserExperienceLevelType } from 'types';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { getPolicyForS3NonVideo, updateUserProfileData } from 'src/apis/user';
import { get } from 'lodash';
import { handleS3Upload } from 'src/utils/imageUpload';
import { useUserContext } from 'src/context/UserProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';

const OptionalForm = () => {
	const toast = useToast();
	const { user } = useUserContext();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const isFromPlacementDrive = searchParams.get('from') === 'placement-drive';
	const isFromPlacementPrep = searchParams.get('from') === 'placement-prep';
	const isFromCompleteProfile = searchParams.get('from') === 'complete-profile';
	const [resume, setResume] = useState<File | null>(null);
	const [panCardDoc, setPanCardDoc] = useState<File | null>(null);
	const [collegeIdCard, setCollegeIdCard] = useState<File | null>(null);
	const [formData, setFormData] = useState<ProfileFormData>({
		resume: user?.profile?.resumeLink || user?.pifMeta?.resumeLink,
		collegeIdCard: user?.pifMeta?.collegeIdCardLink,
		panCardDoc: user?.pifMeta?.panCardDoc,
		expectedCtc: user?.pifMeta?.expectedCtc,
		preferredJobLocations: user?.profile?.preferredJobLocations || [],
		isBacklog: user?.pifMeta?.underGraduate
			? user?.pifMeta?.underGraduate?.isBacklog
			: user?.pifMeta?.postGraduate?.isBacklog,
		linkedInLink: user?.pifMeta?.linkedinUrl || user?.profile?.linkedInProfile,
		githubLink: user?.pifMeta?.githubProfileLink || user?.profile?.githubURL,
		leetCodeLink: user?.pifMeta?.leetcodeProfileLink,
		hackerRankLink: user?.pifMeta?.hackerRankProfileLink,
		gradYear: user?.profile?.gradYear?.toString(),
		expectedJoining: user?.profile?.isImmediateJoiner ?? user?.profile?.expectedJoining ?? '',
	});

	const updateFormData = (field: keyof ProfileFormData, value: string | ScoreType | string[] | boolean) => {
		setFormData(prevData => ({ ...(prevData || {}), [field]: value }) as ProfileFormData);
	};
	const navigate = useNavigate();
	const updateUserProfileDataFn = useMutation({
		mutationFn: updateUserProfileData,
		onSuccess: () => {
			toast({
				title: 'Profile updated',
			});
			queryClient.refetchQueries(['verifyCookie']);
			if (isFromPlacementPrep) {
				navigate('/placement-prep');
			} else if (isFromPlacementDrive) {
				navigate('/dashboard?open=placement-drive');
			} else {
				// navigate('/dashboard');
				navigate('/my-applications');
			}
		},
		onError: (error: Error) => {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		let collegeIdCardUrl = '';
		let panCardDocUrl = '';

		if (collegeIdCard) {
			const fileSizeMB = collegeIdCard.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'College ID Card File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			const policy = await getPolicyForS3NonVideo(
				collegeIdCard.name?.trim()?.replaceAll(' ', '_'),
				'collegeIdCardUrls',
				collegeIdCard.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, collegeIdCard);
			if (uploadedUrl) collegeIdCardUrl = uploadedUrl;
		}

		if (panCardDoc) {
			const fileSizeMB = panCardDoc.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'Pan Card File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			const policy = await getPolicyForS3NonVideo(
				panCardDoc.name?.trim()?.replaceAll(' ', '_'),
				'panCardDocUrls',
				panCardDoc.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, panCardDoc);
			if (uploadedUrl) panCardDocUrl = uploadedUrl;
		}

		if (formData) {
			updateUserProfileDataFn.mutate({
				...formData,
				collegeIdCard: collegeIdCardUrl,
				panCardDoc: panCardDocUrl,
			});
		}
	};

	return (
		<div className="max-w-[1480px] w-full mx-auto px-4 lg:px-[80px] [&_input]:bg-white">
			{isFromPlacementPrep || isFromPlacementDrive ? (
				<div className="text-xl font-semibold text-[#555770] font-inter pt-[28px]">
					<h3>
						We need a few more details from you before you proceed to the{' '}
						{isFromPlacementPrep ? 'placement prep' : isFromPlacementDrive ? 'placement drive' : 'next step'}.
					</h3>
				</div>
			) : null}
			<form className="max-w-[904px] pb-4" onSubmit={handleSubmit}>
				<div className="flex items-end gap-3 pt-[30px]">
					<h2 className="text-xl font-semibold w-fit">Basic details</h2>
					<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
				</div>

				<div className="grid md:grid-cols-2 items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-3">
							<Label htmlFor="expectedJoining" value="Available to join" />
						</div>
						<fieldset className="flex max-w-md gap-4">
							<div className="flex items-start gap-2">
								<Radio
									id="Immediate"
									name="expectedJoining"
									value="true"
									onChange={() => {
										updateFormData('expectedJoining', 'Immediate');
									}}
									required={isFromPlacementDrive || isFromPlacementPrep}
									checked={formData?.expectedJoining === 'Immediate'}
									className="mt-[2px]"
									color="blue"
								/>
								<Label htmlFor="Immediate">Immediately</Label>
							</div>
							<div className="flex items-start gap-2">
								<Radio
									id="Within_1_month"
									name="expectedJoining"
									value="Within_1_month"
									onChange={() => updateFormData('expectedJoining', 'Within_1_month')}
									required={isFromPlacementDrive || isFromPlacementPrep}
									checked={formData?.expectedJoining === 'Within_1_month'}
									className="mt-[2px]"
									color="blue"
								/>
								<Label htmlFor="Within_1_month">Within 1 month</Label>
							</div>
							<div className="flex items-start gap-2">
								<Radio
									id="Within_3_months"
									name="expectedJoining"
									value="Within_3_months"
									onChange={() => updateFormData('expectedJoining', 'Within_3_months')}
									required={isFromPlacementDrive || isFromPlacementPrep}
									checked={formData?.expectedJoining === 'Within_3_months'}
									className="mt-[2px]"
									color="blue"
								/>
								<Label htmlFor="Within_3_months">Within 3 months</Label>
							</div>
						</fieldset>
					</div>
				</div>

				<div className="flex items-start flex-wrap w-full mt-7 gap-2 md:gap-[48px]">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="expectedCTC" value="Expected CTC" />
							<span className="text-[#555770] text-xs"> (annual amount)</span>
						</div>

						<TextInput
							min={0}
							sizing="lg"
							type="number"
							color="purple"
							id="expectedCTC"
							placeholder="1,00,000"
							className="RemoveInputArrow"
							value={Number(formData?.expectedCtc)}
							required={isFromPlacementDrive || isFromPlacementPrep}
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							onChange={e => {
								if (Number(e.target.value) < 0) {
									return;
								}
								updateFormData('expectedCtc', e.target.value?.toString());
							}}
						/>
					</div>

					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="preferredJobLocations" value="Preferred locations" />
						</div>

						<Select
							isMulti
							isClearable
							menuPlacement="auto"
							id="preferredJobLocations"
							classNamePrefix="flowbite-select"
							required={isFromPlacementDrive || isFromPlacementPrep}
							options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
							value={locationPreferenceOptions
								?.filter(location => formData?.preferredJobLocations?.includes(location))
								.map(location => ({ value: location, label: location }))}
							onChange={value => {
								updateFormData(
									'preferredJobLocations',
									value.map(el => el.value),
								);
							}}
						/>
					</div>
				</div>

				<div className="flex items-end gap-3 pt-[30px]">
					<h2 className="text-xl font-semibold w-fit">Education details</h2>
					<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
				</div>

				<div className="flex items-start flex-wrap gap-4 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-3">
							<Label htmlFor="backlogs" value="Backlogs?" />
						</div>
						<fieldset className="flex max-w-md gap-4">
							<div className="flex items-start gap-2">
								<Radio
									id="True"
									name="backlogs"
									value="true"
									onChange={() => {
										updateFormData('isBacklog', 'true');
									}}
									required={isFromPlacementDrive || isFromPlacementPrep}
									checked={formData?.isBacklog === 'true'}
									className="mt-[2px]"
									color="blue"
								/>
								<Label htmlFor="True">
									Yes
									<p className="text-[#6B7280] text-xs mt-1">Yes, I have backlogs.</p>
								</Label>
							</div>
							<div className="flex items-start gap-2">
								<Radio
									id="False"
									name="backlogs"
									value="false"
									onChange={() => updateFormData('isBacklog', 'false')}
									required={isFromPlacementDrive || isFromPlacementPrep}
									checked={formData?.isBacklog === 'false'}
									className="mt-[2px]"
									color="blue"
								/>
								<Label htmlFor="False">
									No
									<p className="text-[#6B7280] text-xs mt-1">No, I don't have backlogs.</p>
								</Label>
							</div>
						</fieldset>
					</div>

					<div className="md:flex-1 w-full">
						{user?.userPreference?.experienceLevel === UserExperienceLevelType.FINAL_YEAR_STUDENT ? (
							<>
								<div className="mb-2">
									<Label htmlFor="collegeIdCard" value="College ID card" />
									{formData?.collegeIdCard ? (
										<a href={formData?.collegeIdCard} target="_blank" className="text-[#7777E7] underline text-xs ml-1">
											(Preview){' '}
										</a>
									) : null}
								</div>
								<FileInput
									id="collegeIdCard"
									sizing="lg"
									accept="image/jpeg, image/png, image/jpg, .pdf"
									required={(isFromPlacementDrive || isFromPlacementPrep) && !formData?.collegeIdCard}
									onChange={e => {
										if (e.target.files) setCollegeIdCard(e.target.files[0]);
									}}
								/>
								<p className="text-xs text-gray-500 italic mt-3">Only pdf, jpg, png files allowed.</p>
							</>
						) : (
							<>
								<div className="mb-2">
									<Label htmlFor="panCardDoc" value="Pan Card" />
									{formData?.panCardDoc ? (
										<a href={formData?.panCardDoc} target="_blank" className="text-[#7777E7] underline text-xs ml-1">
											(Preview){' '}
										</a>
									) : null}
								</div>
								<FileInput
									id="panCardDoc"
									sizing="lg"
									accept="image/jpeg, image/png, image/jpg, .pdf"
									required={(isFromPlacementDrive || isFromPlacementPrep) && !formData?.panCardDoc}
									onChange={e => {
										if (e.target.files) setPanCardDoc(e.target.files[0]);
									}}
								/>
								<p className="text-xs text-gray-500 italic mt-3">Only pdf, jpg, png files allowed.</p>
							</>
						)}
					</div>
				</div>

				<div className="flex items-end gap-3 pt-[30px]">
					<h2 className="text-xl font-semibold w-fit">
						Social links {isFromPlacementDrive || isFromPlacementPrep ? '(Optional)' : ''}
					</h2>
					<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="linkedInLink" value="LinkedIn Profile" />
							{/* {isFromPlacementDrive || isFromPlacementPrep ? (
								<span className="text-[#555770] text-xs"> (optional)</span>
							) : null} */}
						</div>

						<TextInput
							sizing="lg"
							type="url"
							color="purple"
							id="linkedInLink"
							placeholder="https://www.linkedin.com/in/"
							value={formData?.linkedInLink}
							onChange={e => {
								updateFormData('linkedInLink', e.target.value);
							}}
						/>
					</div>
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="githubLink" value="Github Profile" />
							{/* {isFromPlacementDrive || isFromPlacementPrep ? (
								<span className="text-[#555770] text-xs"> (optional)</span>
							) : null} */}
						</div>

						<TextInput
							sizing="lg"
							type="url"
							color="purple"
							id="githubLink"
							placeholder="https://github.com/user-name"
							value={formData?.githubLink}
							onChange={e => {
								updateFormData('githubLink', e.target.value);
							}}
						/>
					</div>
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="leetCodeLink" value="LeetCode Profile" />
							{/* {isFromPlacementDrive || isFromPlacementPrep ? (
								<span className="text-[#555770] text-xs"> (optional)</span>
							) : null} */}
						</div>

						<TextInput
							sizing="lg"
							type="url"
							color="purple"
							id="leetCodeLink"
							placeholder="https://leetcode.com/u/user-name"
							value={formData?.leetCodeLink}
							onChange={e => {
								updateFormData('leetCodeLink', e.target.value);
							}}
						/>
					</div>
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="hackerRankLink" value="HackerRank Profile" />
							{/* {isFromPlacementDrive || isFromPlacementPrep ? (
								<span className="text-[#555770] text-xs"> (optional)</span>
							) : null} */}
						</div>

						<TextInput
							sizing="lg"
							type="url"
							color="purple"
							id="hackerRankLink"
							placeholder="https://www.hackerrank.com/profile/user-name"
							value={formData?.hackerRankLink}
							onChange={e => {
								updateFormData('hackerRankLink', e.target.value);
							}}
						/>
					</div>
				</div>

				<div className="flex items-center gap-2 mt-[43px]">
					{isFromCompleteProfile || isFromPlacementDrive || isFromPlacementPrep ? null : (
						<Button
							onClick={() => {
								// navigate('/dashboard');
								navigate('/my-applications');
							}}
							color="whiteBlueOutline"
							className="font-inter w-full max-w-[167px]"
							size="lg"
						>
							Skip
						</Button>
					)}
					<Button
						className="font-inter w-fit"
						size="lg"
						type="submit"
						color="purplePlaced"
						disabled={updateUserProfileDataFn?.isLoading}
						isProcessing={updateUserProfileDataFn.isLoading}
					>
						Save & Submit
					</Button>
				</div>
			</form>
		</div>
	);
};

export default OptionalForm;
